import { useState, useEffect, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { focusAfterPaint } from '@/utils/dom';

export interface AddCompanyDocumentSaveData {
  documentName: string;
  file: File;
  filename: string;
}

export interface AddCompanyDocumentModalProps {
  open: boolean;
  theme?: string;
  saving?: boolean;
  onSave: (data: AddCompanyDocumentSaveData) => void;
  onCancel: () => void;
}

export function AddCompanyDocumentModal({
  open,
  theme,
  saving = false,
  onSave,
  onCancel,
}: AddCompanyDocumentModalProps) {
  const [documentName, setDocumentName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilename, setSelectedFilename] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form and focus input when modal opens
  useEffect(() => {
    if (open) {
      setDocumentName('');
      setSelectedFile(null);
      setSelectedFilename('');

      // Reset file input value so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      focusAfterPaint(nameInputRef);
    }
  }, [open]);

  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      setSelectedFile(file);
      setSelectedFilename(file.name);

      // Auto-populate document name from filename if empty
      setDocumentName((prev) => {
        if (!prev.trim()) {
          // Remove extension from filename for default name
          return file.name.replace(/\.[^/.]+$/, '');
        }
        return prev;
      });
    }
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedFile) {
      (window as any).App?.showPopupNotification?.('Please select a file to upload.', 'error');
      return;
    }

    if (!documentName.trim()) {
      (window as any).App?.showPopupNotification?.('Document name is required.', 'error');
      return;
    }

    onSave({
      documentName: documentName.trim(),
      file: selectedFile,
      filename: selectedFilename,
    });
  }, [selectedFile, documentName, selectedFilename, onSave]);

  return (
    <BaseFormModal
      open={open}
      title="Add Company Document"
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText={saving ? 'Saving...' : 'Add'}
      saveDisabled={saving}
      busy={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div className="kstc-form-group">
        <label className="kstc-label">File</label>
        {selectedFilename ? (
          <div className="kstc-file-selected">
            <span className="kstc-file-selected__name">{selectedFilename}</span>
            <button
              type="button"
              className="kstc-btn kstc-btn--secondary kstc-btn--sm"
              onClick={handleAttachmentClick}
            >
              Change
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={handleAttachmentClick}
          >
            <svg className="kstc-btn__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Select File
          </button>
        )}
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Document Name</label>
        <input
          ref={nameInputRef}
          type="text"
          className="kstc-input kst-document-name-input"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          placeholder="Enter document name..."
        />
      </div>
    </BaseFormModal>
  );
}

(window as any).AddCompanyDocumentModalReact = AddCompanyDocumentModal;
